import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Bot, Send, Loader2, X, Minus, Sparkles, Mic, MicOff, Maximize2, Minimize2, Volume2, VolumeX, Square, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { isWeatherOrLocationIntent, getBrowserLocation, reverseGeocode } from '../lib/voiceLocation';

const API = '/api';

const QUICK_PROMPTS = [
  { label: 'O que posso fazer?', prompt: 'Quais funcionalidades estao disponiveis para o meu papel?' },
  { label: 'Minhas permissoes', prompt: 'Quais sao minhas permissoes e limitacoes na plataforma?' },
  { label: 'Meu projeto', prompt: 'Quais recursos estao habilitados no meu projeto?' },
  { label: 'Regulamentacao RPAS', prompt: 'Quais sao as principais regras da ICA 100-40 para operacao de drones no Brasil?' },
  { label: 'Manutencao Drone', prompt: 'Qual o checklist de manutencao preventiva para os drones DJI Matrice 30 e Mavic 3E?' },
];

const ProjectAIHelper = () => {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [isRecording, setIsRecording] = useState(false);
  const [playingMsgIdx, setPlayingMsgIdx] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [autoSpeak, setAutoSpeak] = useState(false);
  // Draggable position
  const [btnPos, setBtnPos] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const roleLabel = {
    super_admin: 'Super Admin', admin: 'Administrador', supervisor: 'Supervisor',
    leader: 'Lider', pilot: 'Piloto', vigilant: 'Vigilante'
  }[user?.role] || user?.role;

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (el) el.scrollTop = el.scrollHeight;
      }
    }, 100);
  }, []);

  useEffect(() => { if (open && !minimized) inputRef.current?.focus(); }, [open, minimized]);

  // TTS playback
  const speakText = useCallback(async (text, msgIdx) => {
    // Stop any current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingMsgIdx === msgIdx) {
      setPlayingMsgIdx(null);
      return;
    }
    // Strip markdown for cleaner speech
    const cleanText = text.replace(/[#*_`\[\]()>]/g, '').replace(/\n+/g, '. ').trim();
    if (!cleanText) return;

    setTtsLoading(msgIdx);
    try {
      const res = await axios.post(`${API}/ai/text-to-speech`, { text: cleanText }, { headers });
      const audioSrc = `data:audio/mp3;base64,${res.data.audio_base64}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      setPlayingMsgIdx(msgIdx);
      setTtsLoading(null);
      audio.play();
      audio.onended = () => { setPlayingMsgIdx(null); audioRef.current = null; };
      audio.onerror = () => { setPlayingMsgIdx(null); audioRef.current = null; toast.error('Erro ao reproduzir audio'); };
    } catch {
      setTtsLoading(null);
      toast.error('Erro ao gerar voz');
    }
  }, [headers, playingMsgIdx]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingMsgIdx(null);
  }, []);

  const sendMessage = async (text, opts = {}) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    const userIdx = messages.length;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    scrollToBottom();
    setLoading(true);

    // Build payload - include coordinates if provided (voice auto-geolocation)
    const payload = { message: msg, session_id: sessionId };
    if (opts.latitude != null && opts.longitude != null) {
      payload.latitude = opts.latitude;
      payload.longitude = opts.longitude;
      if (opts.location_label) payload.location_label = opts.location_label;
    }

    try {
      const r = await axios.post(`${API}/ai/project-chat`, payload, { headers });
      const assistantMsg = { role: 'assistant', content: r.data.response };
      setMessages(prev => [...prev, assistantMsg]);
      // Auto-speak response if enabled
      if (autoSpeak && r.data.response) {
        setTimeout(() => speakText(r.data.response, userIdx + 1), 200);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.' }]);
    } finally { setLoading(false); scrollToBottom(); }
  };

  // When voice produces a weather/location question, grab GPS and include coords
  const sendVoiceTranscript = async (transcript) => {
    const txt = (transcript || '').trim();
    if (!txt) return;
    if (!isWeatherOrLocationIntent(txt)) {
      sendMessage(txt);
      return;
    }
    // Show a transparent UX hint in the conversation
    setMessages(prev => [...prev, {
      role: 'system',
      content: '📍 Capturando sua localização atual para o briefing meteorológico...'
    }]);
    try {
      const { latitude, longitude } = await getBrowserLocation();
      const label = await reverseGeocode(latitude, longitude);
      setMessages(prev => {
        const next = [...prev];
        // Replace the placeholder system message with the resolved label
        const idx = next.map(m => m.role).lastIndexOf('system');
        if (idx >= 0) {
          next[idx] = {
            role: 'system',
            content: `📍 Usando sua localização atual${label ? ': **' + label + '**' : ''} (GPS do dispositivo)`
          };
        }
        return next;
      });
      sendMessage(txt, { latitude, longitude, location_label: label });
    } catch (err) {
      // Replace placeholder with permission error hint
      setMessages(prev => {
        const next = [...prev];
        const idx = next.map(m => m.role).lastIndexOf('system');
        if (idx >= 0) {
          next[idx] = {
            role: 'system',
            content: '⚠️ Não foi possível obter sua localização. Prosseguindo sem GPS - mencione a cidade na pergunta.'
          };
        }
        return next;
      });
      sendMessage(txt);
    }
  };

  const toggleVoice = async () => {
    if (isRecording) {
      mediaRecRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'voice.webm');
        try {
          const r = await axios.post(`${API}/ai/voice-to-text`, formData, { headers });
          if (r.data.text) {
            setInput(r.data.text);
            sendVoiceTranscript(r.data.text);
          }
        } catch { toast.error('Erro ao processar voz'); }
      };
      recorder.start();
      mediaRecRef.current = recorder;
      setIsRecording(true);
      toast.info('Gravando voz...');
    } catch { toast.error('Microfone nao disponivel'); }
  };

  // ===== DRAGGABLE BUTTON LOGIC =====
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    dragMoved.current = false;
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      dragMoved.current = true;
      const x = Math.max(0, Math.min(window.innerWidth - 56, e.clientX - dragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 56, e.clientY - dragOffset.current.y));
      setBtnPos({ x, y });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [dragging]);

  // Hide for super_admin (they have the full AI Assistant page)
  if (!user || user.role === 'super_admin') return null;

  const btnStyle = btnPos.x !== null
    ? { position: 'fixed', left: `${btnPos.x}px`, top: `${btnPos.y}px`, zIndex: 9999, touchAction: 'none' }
    : { position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999, touchAction: 'none' };

  // Floating button (draggable)
  if (!open) {
    return (
      <button
        onPointerDown={handlePointerDown}
        onClick={() => { if (!dragMoved.current) setOpen(true); }}
        style={btnStyle}
        className={`w-14 h-14 rounded-full bg-gradient-to-br from-gold to-amber-500 text-navy shadow-lg shadow-gold/30 hover:shadow-xl transition-all flex items-center justify-center select-none ${dragging ? 'scale-110 cursor-grabbing' : 'cursor-grab hover:scale-105'}`}
        data-testid="jorginho-btn"
        aria-label="Abrir Jorginho"
      >
        <Bot size={22} />
      </button>
    );
  }

  // Minimized bar
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999]">
        <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 pl-3 pr-1 py-1 hover:shadow-xl transition-shadow">
          <Bot size={16} className="text-gold" />
          <span className="text-xs font-semibold text-navy">Jorginho</span>
          <Button variant="ghost" size="icon" onClick={() => setMinimized(false)} className="h-8 w-8 rounded-full text-navy hover:bg-gold/10" data-testid="jorginho-expand">
            <Sparkles size={14} />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => { setOpen(false); setMinimized(false); stopAudio(); }} className="h-8 w-8 rounded-full text-gray-400 hover:text-red-500" data-testid="jorginho-close-min">
            <X size={14} />
          </Button>
        </div>
      </div>
    );
  }

  const panelClasses = expanded
    ? 'fixed inset-2 sm:inset-4 z-[9999]'
    : 'fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] w-[calc(100vw-2rem)] sm:w-[400px] h-[min(560px,calc(100vh-3rem))]';

  return (
    <div className={panelClasses} data-testid="jorginho-panel">
      <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden w-full h-full`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-navy to-navy/90 text-white select-none flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
              <Bot size={16} className="text-gold" />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-wide">Jorginho</p>
              <p className="text-[9px] text-white/50 font-mono">{roleLabel} | Assistente IA</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Auto-speak toggle */}
            <Button variant="ghost" size="icon" onClick={() => { setAutoSpeak(!autoSpeak); stopAudio(); }}
              className={`h-7 w-7 rounded-full ${autoSpeak ? 'text-gold bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              data-testid="jorginho-auto-speak" title={autoSpeak ? 'Desativar voz automatica' : 'Ativar voz automatica'}>
              {autoSpeak ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setExpanded(!expanded)} className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10 rounded-full" data-testid="jorginho-fullscreen">
              {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setMinimized(true)} className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10 rounded-full" data-testid="jorginho-minimize">
              <Minus size={14} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { setOpen(false); setMinimized(false); setExpanded(false); stopAudio(); }} className="h-7 w-7 text-white/60 hover:text-red-300 hover:bg-white/10 rounded-full" data-testid="jorginho-close">
              <X size={14} />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-3 min-h-0" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="py-6 px-2">
              <p className="text-navy font-semibold text-sm mb-1">Ola, {user?.name || user?.username}!</p>
              <p className="text-gray-400 text-xs mb-4">Sou o Jorginho, seu assistente na plataforma. Como posso te ajudar?</p>
              <div className="space-y-2">
                {QUICK_PROMPTS.map(qp => (
                  <button key={qp.label} onClick={() => sendMessage(qp.prompt)}
                    className="w-full text-left px-3 py-2.5 rounded-xl border border-gray-150 bg-gray-50/50 hover:bg-gold/5 hover:border-gold/30 transition-colors text-xs text-gray-700 font-medium flex items-center gap-2"
                    data-testid={`jorginho-quick-${qp.label.toLowerCase().replace(/[?\s]/g, '-')}`}>
                    <Sparkles size={12} className="text-gold flex-shrink-0" />
                    {qp.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-3">
              {messages.map((msg, i) => {
                // Render system messages (e.g. GPS capture notice) as centered info bubble
                if (msg.role === 'system') {
                  return (
                    <div key={i} className="flex justify-center" data-testid={`chat-system-${i}`}>
                      <div className="max-w-[90%] rounded-full px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-mono flex items-center gap-1.5">
                        <MapPin size={11} className="text-amber-600 flex-shrink-0" />
                        <ReactMarkdown components={{
                          p: ({children}) => <span>{children}</span>,
                          strong: ({children}) => <strong className="font-semibold text-amber-900">{children}</strong>,
                        }}>{msg.content}</ReactMarkdown>
                      </div>
                    </div>
                  );
                }
                return (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    msg.role === 'user' ? 'bg-navy text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}>
                    {msg.role === 'user' ? msg.content : (
                      <ReactMarkdown components={{
                        p: ({children}) => <p className="mb-1.5 last:mb-0">{children}</p>,
                        strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-1.5 space-y-0.5">{children}</ul>,
                        li: ({children}) => <li className="text-[13px]">{children}</li>,
                      }}>{msg.content}</ReactMarkdown>
                    )}
                    {/* TTS button for each message */}
                    <div className="flex justify-end mt-1.5">
                      <button
                        onClick={() => speakText(msg.content, i)}
                        disabled={ttsLoading === i}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] transition-colors ${
                          playingMsgIdx === i
                            ? 'bg-gold/20 text-gold'
                            : msg.role === 'user'
                              ? 'text-white/50 hover:text-white/80 hover:bg-white/10'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`tts-btn-${i}`}
                        title={playingMsgIdx === i ? 'Parar' : 'Ouvir'}
                      >
                        {ttsLoading === i ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : playingMsgIdx === i ? (
                          <><Square size={10} /> Parar</>
                        ) : (
                          <><Volume2 size={10} /> Ouvir</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-3.5 py-2.5">
                    <Loader2 size={14} className="text-gold animate-spin" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="px-3 py-3 border-t border-gray-200 flex-shrink-0">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
            <Input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              placeholder="Pergunte ao Jorginho..." className="flex-1 h-9 rounded-xl border-gray-200 text-sm"
              disabled={loading} data-testid="jorginho-input" />
            <Button type="button" onClick={toggleVoice}
              className={`h-9 w-9 rounded-xl p-0 flex-shrink-0 ${isRecording ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              data-testid="jorginho-voice-btn">
              {isRecording ? <MicOff size={14} /> : <Mic size={14} />}
            </Button>
            <Button type="submit" disabled={!input.trim() || loading}
              className="bg-gold hover:bg-gold/90 text-navy h-9 w-9 rounded-xl p-0 flex-shrink-0" data-testid="jorginho-send">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectAIHelper;
